import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import EmailService from './pages/EmailService'
import PushService from './pages/PushService'
import WhatsAppService from './pages/WhatsAppService'
import ShortlinkService from './pages/ShortlinkService'
import SmsService from './pages/SmsService'
import OtpService from './pages/OtpService'

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return children
}

// Public Route Component (redirects to dashboard if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

// Routes wrapper that uses auth context
function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      {/* Self-serve email/password registration removed — SSO-only. */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/service/otp" element={<Navigate to="/service-otp" replace />} />
      <Route path="/service/otp/*" element={<Navigate to="/service-otp" replace />} />
      <Route
        path="/service-otp/*"
        element={
          <ProtectedRoute>
            <OtpService />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-email/*"
        element={
          <ProtectedRoute>
            <EmailService />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-push/*"
        element={
          <ProtectedRoute>
            <PushService />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-whatsapp/*"
        element={
          <ProtectedRoute>
            <WhatsAppService />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-shortlink/*"
        element={
          <ProtectedRoute>
            <ShortlinkService />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-sms/*"
        element={
          <ProtectedRoute>
            <SmsService />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}

export default App
